import React from "react";
import {
  Bundle,
  BundleLine,
  BundleLineVariant,
  DiscountStrategySchema,
  getPrice,
  VenueProduct,
} from "@greeter/core";
import {
  ErrorHint,
  FloatingBackButton,
  GradientButton,
  PageContainer,
  Props,
  Spinner,
} from "@greeter/matter";
import {
  IonAlert,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonToggle,
  useIonAlert,
  useIonRouter,
  useIonToast,
  useIonViewWillEnter,
} from "@ionic/react";
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { BundleChange, editBundleMachine, hasError } from "./EditBundleMachine";
import { useActor, useSelector } from "@xstate/react";
import { FetchProduct, IVenueApi } from "@greeter/venue-api";
import { adapt, unsubscriber } from "@greeter/xstate-util";
import { ProductLibrary } from "@greeter-venue/components/ProductLibrary";
import { imageOutline, close, warning } from "ionicons/icons";
import {
  HiddenUploadInput,
  InputController,
} from "@greeter-venue/components/UploadInput";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { classNames, doNothing } from "@greeter/util";
import { Signal, useSignal } from "@greeter/event";
import { useHistory } from "react-router-dom";
import { Routes } from "@greeter-venue/utility/Routes";
import { Price, PriceWithoutVat, vat } from "@greeter/commerce";
import {
  SpecialPricePeriodApiRequirements,
  SpecialPricePeriodEditor,
} from "./SpecialPricePeriodEditor";
import { Section } from "./Section";
import { Option } from "./Option";
import {
  ActivePeriodsEditor,
  ActivePeriodsEditorApiRequirements,
} from "./ActivePeriodsEditor";
import { CSS } from "@dnd-kit/utilities";
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TraversalOrder,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { ProductLibraryApiRequirements } from "@greeter-venue/components/ProductLibrary/ProductLibraryMachine";
import DangerText from "@greeter-venue/components/DangerText";
import { isNumber } from "lodash";
import { FeatureFlags } from "@greeter/core";
import { formatMoney } from "@greeter/formatters";
import useMeasure from "react-use-measure";
import { motion } from "motion/react";
import css from "./EditBundlePage.module.scss";
import {
  replaceDefaultOnInputPlugin,
  setCursorBeforeSuffixPlugin,
} from "@greeter/maskito";
import { IconGripVertical } from "@tabler/icons-react";
import { PriceInput, TextInput, TextInputProps } from "@greeter/matter-v2";

function nothingStrategy() {
  return { x: 0, y: 0, scaleX: 1, scaleY: 1 };
}

interface BundleSummaryApiRequirements {
  fetchProduct: FetchProduct;
}

type BundleSummaryProps = {
  bundle: Bundle;
  refreshSignal: Signal;
  api: BundleSummaryApiRequirements;
};
function BundleSummary(props: BundleSummaryProps) {
  const [maxCost, setMaxCost] = useState<PriceWithoutVat>(0);
  const [minCost, setMinCost] = useState<PriceWithoutVat>(0);
  const [maxPrice, setMaxPrice] = useState<Price>(0);
  const [loading, setLoading] = useState(false);
  const productCache = useMemo(() => new Map<string, VenueProduct>(), []);

  useEffect(() => {
    async function cacheProducts() {
      console.group("[cacheProducts]");
      console.debug("caching products for bundle", props.bundle);
      for (const line of props.bundle.lines) {
        for (const variation of line.variants) {
          const existing = productCache.get(variation.productId);

          if (existing) {
            continue;
          }
          const product = await props.api.fetchProduct(variation.productId);
          productCache.set(product.id, product);
        }
      }
      console.groupEnd();
    }

    function summarizeCost() {
      console.group("[summarizeCost]");
      console.debug("summarizing lines with product cache", productCache);
      for (const line of props.bundle.lines) {
        let max: number = 0;
        let min: number = Number.MAX_SAFE_INTEGER;

        console.debug("summarizing line", line);

        for (const variation of line.variants) {
          const product = productCache.get(variation.productId);
          if (!product || !product.cost) continue;

          const cost = product.cost;

          if (cost > max) max = cost;
          if (cost < min) min = cost;
        }

        let maxResult = max * line.quantity;
        let minResult =
          (min === Number.MAX_SAFE_INTEGER ? 0 : min) * line.quantity;

        setMinCost((prev) => prev + minResult);
        setMaxCost((prev) => prev + maxResult);
      }
      console.groupEnd();
    }

    function summarizeMaxPrice() {
      console.group("[summarizeMaxPrice]");
      console.debug("summarizing lines with product cache", productCache);
      for (const line of props.bundle.lines) {
        let max: number = 0;

        for (const variation of line.variants) {
          const product = productCache.get(variation.productId);
          if (!product || !product.price) {
            continue;
          }

          const price = variation.additionalPrice ?? 0;

          if (price > max) max = price;
        }

        const maxResult = max * line.quantity;

        setMaxPrice((prev) => prev + maxResult);
        console.debug("maxResult", maxResult);
      }
      console.groupEnd();
    }

    setMinCost(0);
    setMaxCost(0);
    setMaxPrice(0);
    setLoading(true);
    cacheProducts()
      .then(summarizeCost)
      .then(summarizeMaxPrice)
      .finally(() => setLoading(false));
  }, [props.refreshSignal, props.bundle]);

  const bundlePrice = getPrice(props.bundle);

  console.log("[BundleSummary]", bundlePrice);

  return (
    <div className={css.BundleSummary}>
      <h1>Total</h1>
      <h1>
        {bundlePrice.type === "discounted" ? (
          <span>
            <s>{formatMoney(bundlePrice.price)} </s>
            {formatMoney(bundlePrice.discountedPrice)}
          </span>
        ) : (
          formatMoney(bundlePrice.price)
        )}{" "}
        kr.
      </h1>

      {!loading && minCost !== maxCost ? (
        <>
          <p>Min. kostpris</p>
          <p>{formatMoney(minCost)} kr.</p>

          <p>Max. kostpris</p>
          <p>{formatMoney(maxCost)} kr.</p>
        </>
      ) : (
        <>
          <p>Kostpris</p>
          <p>{formatMoney(maxCost)} kr.</p>
        </>
      )}

      {props.bundle.price && (
        <>
          <p>Moms (25%)</p>
          <p>
            {formatMoney(
              vat(
                bundlePrice.type === "discounted"
                  ? bundlePrice.discountedPrice
                  : bundlePrice.price
              )
            )}{" "}
            kr.
          </p>

          <p style={{ color: "var(--gm-color-primary-highlight)" }}>
            Avance (efter moms)
          </p>
          <p style={{ color: "var(--gm-color-primary-highlight)" }}>
            {formatMoney(
              (bundlePrice.type === "discounted"
                ? bundlePrice.discountedPrice
                : bundlePrice.price) -
                vat(bundlePrice.price) -
                maxCost
            )}{" "}
            kr.
          </p>

          {maxPrice > 0 && props.bundle.price !== maxPrice && (
            <>
              <p
                style={{
                  margin: 0,
                  color: "var(--gm-color-primary-highlight)",
                }}
              >
                Potentiel avance (med merpris)
              </p>
              <p
                style={{
                  margin: 0,
                  color: "var(--gm-color-primary-highlight)",
                }}
              >
                {formatMoney(
                  (bundlePrice.type === "discounted"
                    ? bundlePrice.discountedPrice
                    : bundlePrice.price) -
                    vat(bundlePrice.price) +
                    (maxPrice - vat(maxPrice))
                )}{" "}
                kr.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}

type QuantityInputProps = {
  id?: string;
  value?: number;
  className?: string;
  style?: React.CSSProperties;
  onChange?: (quantity?: number) => void;
};
function QuantityInput(props: QuantityInputProps) {
  const [value, setValue] = useState("");

  function setValueOnInput() {
    if (props.value !== undefined) {
      setValue(`${props.value}`);
    }
  }

  useEffect(setValueOnInput, []);

  return (
    <input
      id={props.id}
      className={`${css.Input} ${css.QuantityInput} ${props.className}`}
      style={props.style}
      value={value}
      type="number"
      inputMode="numeric"
      onBlur={setValueOnInput}
      onInput={(ev) => {
        setValue(ev.currentTarget.value);
        props.onChange?.(
          ev.currentTarget.value.trim() !== ""
            ? Number(ev.currentTarget.value)
            : undefined
        );
      }}
    />
  );
}

const pctRegex = /^([\d.,]+) %$/;
export function extractPctFromInput(raw: string): number {
  const result = raw.match(pctRegex);
  if (result) {
    // NOTE: Broken into steps for easy debugging.
    // Avoid the temptation to chain this.
    let numberString = result[1];
    numberString = numberString.replace(/\./, "");
    numberString = numberString.replace(/,/, ".");
    return Number(numberString);
  }
  return 0;
}

const pctPostfix = " %";
let percentInputMask = maskitoNumberOptionsGenerator({
  postfix: pctPostfix,
  thousandSeparator: ".",
  decimalSeparator: ",",
  max: 100,
  min: 1,
});
percentInputMask = {
  ...percentInputMask,
  plugins: [
    setCursorBeforeSuffixPlugin(pctPostfix),
    replaceDefaultOnInputPlugin({ suffix: pctPostfix, default: "-" }),
  ],
};
export type PercentInputProps = Props & {
  value?: number;
  onChange: (v?: number) => void;
};
export function PercentInput(props: PercentInputProps) {
  const inputRef = useMaskito({ options: percentInputMask });
  const _inputRef = useRef<HTMLInputElement | null>(null);

  function setInitValueOnInput() {
    if (_inputRef.current) {
      _inputRef.current.value = `${props.value ?? "-"} %`;
    }
  }

  useEffect(setInitValueOnInput, []);
  useEffect(() => {
    _inputRef.current?.addEventListener("focus", setInitValueOnInput);
    _inputRef.current?.addEventListener("blur", setInitValueOnInput);

    return () => {
      _inputRef.current?.removeEventListener("focus", setInitValueOnInput);
      _inputRef.current?.removeEventListener("blur", setInitValueOnInput);
    };
  }, [props.value]);

  return (
    <input
      {...classNames(css.Input)}
      onInput={(ev) => {
        if (props.onChange && ev.currentTarget.value.trim() !== "") {
          const pct = extractPctFromInput(ev.currentTarget.value);
          props.onChange(pct);
        } else if (props.onChange) {
          props.onChange(undefined);
        }
      }}
      ref={(r) => {
        inputRef(r);
        _inputRef.current = r;
      }}
      inputMode="numeric"
      data-testid={props["data-testid"]}
      style={props.style}
    />
  );
}

type BundleLineSectionTitleProps = TextInputProps;
function BundleLineSectionTitle(props: BundleLineSectionTitleProps) {
  return <TextInput className={css.BundleLineSectionTitle} {...props} />;
}

type BundleLineSectionProps = PropsWithChildren & {
  id: string;
  style?: React.CSSProperties;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  onBlur?: () => void;
};
function BundleLineSection({ className, ...props }: BundleLineSectionProps) {
  return <div {...classNames(css.BundleLineSection, className)} {...props} />;
}

type BundleLineHeaderProps = PropsWithChildren & { className?: string | false };
function BundleLineHeader({ className, ...props }: BundleLineHeaderProps) {
  return <div {...classNames(className, css.BundleLineHeader)} {...props} />;
}

type BundleLineVariantHeaderProps = {
  featureFlags?: FeatureFlags;
};
function BundleLineVariantHeader(props: BundleLineVariantHeaderProps) {
  return (
    <div
      {...classNames(
        css.BundleLineVariantHeader,
        css.BundleLineVariantRow,
        css.BundlePickerV2
      )}
    >
      <h3>Variant</h3>
      <h3>Mængde</h3>
      <h3>Merpris</h3>
      <h3></h3>
    </div>
  );
}

type RemoveLineButtonProps = {
  onClick?: () => void;
  style?: React.CSSProperties;
};
function RemoveLineButton(props: RemoveLineButtonProps) {
  return (
    <div className={css.Remove} onClick={props.onClick}>
      <IonIcon
        icon={close}
        style={{
          width: "1rem",
          height: "1rem",
          color: "var(--gm-color-dark-primary)",
        }}
      />
    </div>
  );
}

type BundleLineVariantInputChangeEvent = Partial<{
  productId: string;
  quantity: number;
  additionalPrice: { amount: number };
}>;

type BundleLineVariantInputProps = PropsWithChildren & {
  onChange?: (event: BundleLineVariantInputChangeEvent) => void;
  onRemove?: (variantId: string) => void;
  onBlur?: () => void;
  variant: BundleLineVariant;
  featureFlags?: FeatureFlags;
};
function BundleLineVariantInput(props: BundleLineVariantInputProps) {
  return (
    <div
      key={props.variant.id}
      {...classNames(
        css.BundleLineVariantInput,
        css.BundleLineVariantRow,
        css.BundlePickerV2
      )}
    >
      <p className={css.VariantTitle}>{props.variant.name}</p>

      <QuantityInput
        className={css.Cell}
        value={props.variant.quantity ?? 0}
        style={{ fontSize: 14 }}
        onChange={(quantity) => {
          props.onChange?.({ quantity });
        }}
      />

      <PriceInput
        className={css.Cell}
        onBlur={props.onBlur}
        value={props.variant.additionalPrice}
        style={{ fontSize: 14, padding: 10 }}
        onChange={(amount?: number) =>
          props.onChange?.({ additionalPrice: { amount: amount ?? 0 } })
        }
      />

      <RemoveLineButton
        style={{ borderLeft: "unset" }}
        onClick={() => props.onRemove?.(props.variant.id)}
      />
    </div>
  );
}

type AddBundleLineButtonProps = {
  onClick?: () => void;
  className?: string;
};
function AddBundleLineButton(props: AddBundleLineButtonProps) {
  return (
    <div
      {...classNames(props.className, css.AddBundleLineButton)}
      onClick={props.onClick}
      data-testid={props["data-testid"]}
    >
      + Tilføj produktlinje
    </div>
  );
}

type AddBundleLineVariantButtonProps = PropsWithChildren & {
  onClick: () => void;
  className?: string;
};
function AddBundleLineVariantButton(props: AddBundleLineVariantButtonProps) {
  return (
    <div
      {...classNames(css.AddBundleLineVariantButton, props.className)}
      onClick={props.onClick}
      data-testid={props["data-testid"]}
    >
      {props.children ? props.children : "+ Tilføj produktvariant"}
    </div>
  );
}

function SortableBundleLine({
  id,
  line: l,
  onOpenProductPicker,
  onChange: change,
  onRefreshSummary,
  error,
  onBlur,
  featureFlags,
}: {
  id?: string;
  error?: string;
  line: BundleLine;
  onOpenProductPicker: () => void;
  onRefreshSummary: () => void;
  onChange: (c: BundleChange) => void;
  onBlur?: () => void;
  hideVariants?: boolean;
  tilt: boolean;
  featureFlags?: FeatureFlags;
}) {
  const draggable = useSortable({
    id: l.id,
    strategy: nothingStrategy,
  });
  // NOTE: https://github.com/clauderic/dnd-kit/issues/1118
  // This is the setup to ensure sizing and reordering is correct
  const style = draggable.transform
    ? {
        transform:
          CSS.Translate.toString(draggable.transform) +
          (draggable.isDragging ? " rotate(2deg)" : " rotate(0deg)"),
        transition: draggable.transition,
        opacity: draggable.isOver ? 0.6 : 1,
      }
    : undefined;

  const [active, setActive] = useState<string>();
  const activeVariant = l.variants.find((v) => v.id === active);

  const sensors = useSensors(useSensor(PointerSensor));

  const [errorRef, errorMeasure] = useMeasure();

  return (
    <BundleLineSection
      id={id ?? l.id}
      ref={draggable.setNodeRef}
      key={"line-" + l.id}
      style={{ position: "relative", ...style }}
      onBlur={onBlur}
      {...classNames(error && "error-outline")}
      {...draggable.attributes}
    >
      <BundleLineHeader className={l.variants.length === 1 && css.Single}>
        <div
          ref={draggable.setActivatorNodeRef}
          {...draggable.listeners}
          className={css.GrabButton}
          style={{ touchAction: "none" }}
        >
          <IconGripVertical />
        </div>
        <BundleLineSectionTitle
          value={l.name}
          onChange={function onChange(ev) {
            change({
              type: "setBundleLineName",
              data: {
                bundleLineId: l.id,
                name: ev.target.value,
              },
            });
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <label
            htmlFor={`${l.id}-quantity`}
            style={{
              fontSize: "var(--gm-text-sm)",
            }}
          >
            {l.variants.length === 1 ? "Antal" : "Vælg"}
          </label>
          <QuantityInput
            id={`${l.id}-quantity`}
            value={l.quantity}
            onChange={(quantity) => {
              if (quantity) {
                change({
                  type: "setBundleLineQuantity",
                  data: { quantity, bundleLineId: l.id },
                });
                onRefreshSummary();
              }
            }}
          />
        </div>
        <RemoveLineButton
          onClick={() => {
            change({
              type: "removeBundleLine",
              data: l.id,
            });
            onRefreshSummary();
          }}
        />
      </BundleLineHeader>
      {l.variants.length > 1 && (
        <div className={css.BundleLineVariantContent}>
          {
            /*(!draggable.isSorting && !hideVariants) this is jumpy*/ true && (
              <>
                <BundleLineVariantHeader featureFlags={featureFlags} />
                <DndContext
                  sensors={sensors}
                  onDragStart={(ev) => setActive(ev.active.id as string)}
                  onDragEnd={(ev) => {
                    setActive(undefined);
                    change({
                      type: "reorderBundleLineVariant",
                      data: {
                        bundleLineVariantId: ev.active.id.toString(),
                        bundleLineVariantIdToInsertAt:
                          ev.over?.id.toString() ?? "",
                      },
                    });
                  }}
                >
                  <SortableContext
                    items={l.variants}
                    strategy={nothingStrategy}
                  >
                    {l.variants.map((v) => {
                      return (
                        <SortableBundleLineVariant
                          featureFlags={featureFlags}
                          variant={v}
                          style={{
                            opacity: active && active === v.id ? 0.5 : 1,
                          }}
                          className={
                            error && (v.quantity ?? 0) > 0 && "warning-outline"
                          }
                          onBlur={onBlur}
                          onChange={(ev) => {
                            if (ev.additionalPrice) {
                              change({
                                type: "setAdditionalPrice",
                                data: {
                                  variationId: v.id,
                                  additionalPrice: ev.additionalPrice,
                                },
                              });
                              onRefreshSummary();
                            }

                            if (isNumber(ev.quantity)) {
                              change({
                                type: "setQuantity",
                                data: {
                                  variationId: v.id,
                                  quantity: ev.quantity,
                                },
                              });
                            }
                          }}
                          onRemove={(id) => {
                            change({
                              type: "removeBundleLineVariant",
                              data: id,
                            });
                            onRefreshSummary();
                          }}
                        />
                      );
                    })}
                  </SortableContext>
                  <DragOverlay>
                    {active && activeVariant && (
                      <SortableBundleLineVariant
                        featureFlags={featureFlags}
                        variant={activeVariant}
                      />
                    )}
                  </DragOverlay>
                </DndContext>
              </>
            )
          }
          <AddBundleLineVariantButton onClick={() => onOpenProductPicker()} />
        </div>
      )}
      {l.variants.length === 1 && (
        <AddBundleLineVariantButton onClick={() => onOpenProductPicker()} />
      )}

      {error && (
        <motion.div
          layout="position"
          ref={errorRef}
          style={{ top: -errorMeasure.height - 15 }}
          className={`error-outline ${css.ErrorHintContainer}`}
        >
          <ErrorHint className={css.ErrorHint}>
            <IonIcon icon={warning} /> {error}
          </ErrorHint>
        </motion.div>
      )}
    </BundleLineSection>
  );
}

function SortableBundleLineVariant(
  props: BundleLineVariantInputProps & {
    variant: BundleLineVariant;
    tilt?: boolean;
    featureFlags?: FeatureFlags;
    style?: React.CSSProperties;
    className?: string | false;
    onBlur?: () => void;
  }
) {
  const sortable = useSortable({
    id: props.variant.id,
    strategy: nothingStrategy,
  });
  const style = sortable.transform
    ? {
        transform:
          CSS.Translate.toString(sortable.transform) +
          (sortable.isDragging ? " rotate(2deg)" : " rotate(0deg)"),
        transition: sortable.transition,
        zIndex: sortable.isDragging ? 999 : undefined,
        opacity: sortable.isOver || sortable.isDragging ? 0.6 : 1,
        visibility: sortable.isDragging ? ("none" as any) : undefined,
      }
    : undefined;

  return (
    <motion.div
      layout="position"
      {...classNames(css.SortableBundleLineVariant, props.className)}
      ref={sortable.setNodeRef}
      style={style}
      key={`bundle-line-variant-${props.variant.id}`}
      onBlur={props.onBlur}
      {...sortable.attributes}
    >
      <div
        ref={sortable.setActivatorNodeRef}
        {...sortable.listeners}
        className={css.GrabButton}
        style={{ touchAction: "none" }}
      >
        <IconGripVertical style={{ width: 16 }} />
      </div>
      <BundleLineVariantInput
        featureFlags={props.featureFlags}
        variant={props.variant}
        onChange={props.onChange}
        onRemove={props.onRemove}
        onBlur={props.onBlur}
      />
    </motion.div>
  );
}

export interface EditBundleApiRequirements
  extends ProductLibraryApiRequirements,
    SpecialPricePeriodApiRequirements,
    ActivePeriodsEditorApiRequirements,
    Pick<
      IVenueApi,
      | "fetchCategories"
      | "saveActivePeriods"
      | "deleteActivePeriods"
      | "fetchBundle"
      | "saveBundle"
      | "saveSpecialPricePeriods"
      | "deleteSpecialPricePeriods"
      | "uploadAsset"
      | "fetchProduct"
      | "deleteBundle"
      | "fetchIntegrationBySlug"
      | "saveOnlinePOSProductConnection"
      | "fetchOnlinePOSProductConnection"
    > {}

function provideApiForActor(
  m: typeof editBundleMachine,
  api: EditBundleApiRequirements
) {
  return m.provide({
    actors: {
      fetchOnlinePOSIntegration: adapt(() =>
        api.fetchIntegrationBySlug("onlinepos")
      ),
      saveOnlinePOSProductConnection: adapt(
        api.saveOnlinePOSProductConnection.bind(api)
      ),
      fetchOnlinePOSProductConnection: adapt(
        api.fetchOnlinePOSProductConnection.bind(api)
      ),
      fetchBundle: adapt(api.fetchBundle.bind(api)),
      uploadAsset: adapt(api.uploadAsset.bind(api)),
      saveBundle: adapt(api.saveBundle.bind(api)),
      deleteBundle: adapt(api.deleteBundle.bind(api)),
      saveActivePeriods: adapt(api.saveActivePeriods.bind(api)),
      deleteActivePeriods: adapt(api.deleteActivePeriods.bind(api)),
      saveSpecialPricePeriods: adapt(api.saveSpecialPricePeriods.bind(api)),
      deleteSpecialPricePeriods: adapt(api.deleteSpecialPricePeriods.bind(api)),
    },
  });
}

/**
 * Fixes the issue with autoscrolling when using dnd-kit with ion-content.
 * This DndContext is able to use this container instead of the
 * web-component scroll element provided by ionic.
 */
function DndAutoScrollFixContainer({
  style,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div style={{ height: "100%", overflowY: "scroll", ...style }} {...props} />
  );
}

type HintProps = PropsWithChildren & {};
function Hint(props: HintProps) {
  return <ErrorHint className={css.Hint} {...props} />;
}

export type EditBundlePageProps = {
  bundleId?: string;
  onSave?: (bundle: Bundle) => Promise<Bundle>;
  onDelete?: (bundle: Bundle) => Promise<void>;
  api: EditBundleApiRequirements;
  featureFlags?: FeatureFlags;
};

export function EditBundlePage(props: EditBundlePageProps) {
  const history = useHistory();
  const router = useIonRouter();
  const contentRef = useRef<HTMLIonContentElement>(null);
  const [snap, send, m] = useActor(
    provideApiForActor(editBundleMachine, props.api),
    {
      input: { bundleId: props.bundleId, featureFlags: props.featureFlags },
    }
  );
  const changedBundle = useSelector(m, (s) => s.context.changes.value);

  function validate() {
    send({ type: "validate" });
  }

  useEffect(
    () =>
      send({ type: "setFeatureFlags", data: props.featureFlags ?? new Map() }),
    [props.featureFlags]
  );

  const validatorTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  function change(_change: BundleChange) {
    send({ type: "change", data: _change });

    if (validatorTimeoutRef.current) clearTimeout(validatorTimeoutRef.current);
    validatorTimeoutRef.current = setTimeout(validate, 250);
  }

  const isUpdating = !changedBundle?.id || changedBundle?.id === "";

  useIonViewWillEnter(() => {
    contentRef.current?.scrollToTop();
  }, []);

  useIonViewWillEnter(() => {
    if (!snap.context.changes) return;
    send({ type: "reset" });
  }, [snap.context.changes]);

  const [toast] = useIonToast();
  const [alert] = useIonAlert();

  useEffect(
    () =>
      m.on("savedBundle", () => {
        const id = changedBundle.id;
        // send({ type: "reset" });
        router.push(
          Routes.mainGroup.venueTab.editBundleCompleted.route({
            bundleId: id,
          }),
          "forward",
          "push",
          { unmount: true }
        );
      }).unsubscribe
  );

  useEffect(() => {
    const subs = [
      m.on("validationFailed", function onValidationFailed() {
        if (!contentRef.current) return;

        const opts: ScrollIntoViewOptions = { behavior: "smooth" };

        const el = contentRef.current;

        for (const err of snap.context.validationErrors) {
          if (err.type === "missing-cover") {
            el.querySelector("#cover")?.scrollIntoView(opts);
          } else if (err.type === "missing-title") {
            el.querySelector("#title")?.scrollIntoView(opts);
          } else if (err.type === "missing-lines") {
            el.querySelector("#lines")?.scrollIntoView(opts);
          } else if (
            err.type === "missing-price" ||
            err.type === "discount-price-larger-than-price"
          ) {
            el.querySelector("#price")?.scrollIntoView(opts);
          } else if (err.type === "invalid-quantity") {
            el.querySelector(
              `#bundle-lines-${err.data.bundleLineId}`
            )?.scrollIntoView(opts);
          }
        }
        console.debug("validationFailed", snap.context.validationErrors);
      }),

      m.on("savingBundleFailed", function onSavingBundleFailed() {
        toast("Kunne ikke gemme bordpakke.", 2_000);
      }),

      m.on("deletedBundle", function onDeletedBundle() {
        router.push(Routes.mainGroup.venueTab.root.route());
      }),
    ];

    return unsubscriber(subs);
  }, [snap, m, history]);

  const fileCtrl = useRef<InputController>(null);

  const refreshSummarySignal = useSignal();

  // Note: IonAlert is made in a way where it waits for the
  // isOpen prop to change to show the alert, so we need just a little time to
  // where the alert is in a false state before allowing for it to turn true. Otherwise
  // if the statemachine turns to true too quickly, the IonAlert might not show up
  // even when in a failed state.
  //
  // Docs can be found here https://ionicframework.com/docs/api/alert#using-isopen
  const [canShowAlert, setCanShowAlert] = useState(false);
  useEffect(() => void setTimeout(() => setCanShowAlert(true), 1_000), []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      // Disable smooth scrolling in Cypress automated tests
      scrollBehavior: "Cypress" in window ? "auto" : undefined,
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [active, setActive] = useState("");
  const activeLine = snap.context.changes.value.lines.find(
    (l) => l.id === active
  );

  const scrollElement = useRef<HTMLElement | undefined | null>(null);

  useEffect(() => {
    contentRef.current
      ?.getScrollElement()
      .then((el) => (scrollElement.current = el));
  }, []);

  return (
    <IonPage>
      <FloatingBackButton />
      <IonContent ref={contentRef} key={changedBundle.id} scrollEvents>
        <DndAutoScrollFixContainer>
          <PageContainer>
            <div className={css.EditBundlePage}>
              <h3 style={{ margin: "3rem 1rem" }}>
                <b>
                  {isUpdating ? "OPDATER" : "OPRET"}
                  <span style={{ color: "var(--gm-color-primary-highlight)" }}>
                    <b> BORDPAKKE</b>
                  </span>
                </b>
              </h3>

              <section className={css.InputSection}>
                <div
                  id="cover"
                  onClick={() => {
                    if (!fileCtrl.current) return;
                    fileCtrl.current.show();
                  }}
                  {...classNames(
                    css.CoverImage,
                    hasError(snap.context.validationErrors, "missing-cover") &&
                      "error-outline"
                  )}
                >
                  <HiddenUploadInput
                    onFile={(f) => {
                      if (f) {
                        send({ type: "uploadAsset", data: f });
                      }
                    }}
                    onController={(ctrl) => {
                      fileCtrl.current = ctrl;
                    }}
                  />
                  {changedBundle?.coverAsset?.uri && (
                    <img
                      className={css.Image}
                      src={changedBundle?.coverAsset.uri}
                    />
                  )}
                  <div className={css.ImageText}>
                    <IonIcon className={css.ImageIcon} icon={imageOutline} />
                    <h4 style={{ margin: 0 }}>
                      Tryk for at uploade et billede
                    </h4>
                  </div>
                </div>
                {hasError(snap.context.validationErrors, "missing-cover") && (
                  <Hint>Husk at uploade et billede til bordpakken.</Hint>
                )}
                <Section id="title">
                  <Section.Title>Titel</Section.Title>
                  <input
                    {...classNames(
                      css.Input,
                      hasError(
                        snap.context.validationErrors,
                        "missing-title"
                      ) && "error-outline"
                    )}
                    value={changedBundle.title}
                    onInput={(ev) =>
                      change({ type: "setTitle", data: ev.currentTarget.value })
                    }
                  />
                  {hasError(snap.context.validationErrors, "missing-title") && (
                    <Hint>Husk at give bordpakken et navn.</Hint>
                  )}
                </Section>

                <Section id="lines">
                  <Section.Title>Indhold</Section.Title>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <DndContext
                      autoScroll={{
                        layoutShiftCompensation: true,
                        enabled: true,
                        order: TraversalOrder.ReversedTreeOrder,
                        // canScroll(el) {
                        //   console.log("[canScroll]", el);
                        //   return true;
                        // },
                      }}
                      // @ts-ignore
                      scrollableContainers={
                        contentRef.current ? [contentRef.current] : []
                      }
                      onDragStart={(event) => {
                        setActive(event.active.id as string);
                      }}
                      onDragEnd={(event) => {
                        change({
                          type: "reorderBundleLine",
                          data: {
                            bundleLineId: event.active.id.toString(),
                            bundleLineIdToInsertAt:
                              event.over?.id.toString() ?? "",
                          },
                        });
                      }}
                      sensors={sensors}
                      collisionDetection={closestCorners}
                    >
                      <SortableContext items={changedBundle.lines}>
                        {changedBundle?.lines.map((l) => (
                          <SortableBundleLine
                            id={`bundle-lines-${l.id}`}
                            featureFlags={props.featureFlags}
                            line={l}
                            tilt={false}
                            onBlur={validate}
                            error={
                              hasError(
                                snap.context.validationErrors,
                                "invalid-quantity",
                                (e) =>
                                  e.type === "invalid-quantity" &&
                                  e.data.bundleLineId === l.id
                              )
                                ? 'Du skal matche totalen af "Mængde" kolonnen med "Vælg" antallet!'
                                : undefined
                            }
                            onRefreshSummary={() => refreshSummarySignal.emit()}
                            onChange={change}
                            onOpenProductPicker={() =>
                              send({
                                type: "openProductPicker",
                                data: { bundleLineId: l.id },
                              })
                            }
                          />
                        ))}
                      </SortableContext>
                      <DragOverlay>
                        {active && activeLine && (
                          <SortableBundleLine
                            id={`dragging-bundle-lines-${activeLine.id}`}
                            featureFlags={props.featureFlags}
                            line={activeLine}
                            tilt={false}
                            onOpenProductPicker={doNothing}
                            onRefreshSummary={doNothing}
                            onChange={doNothing}
                          />
                        )}
                      </DragOverlay>
                    </DndContext>

                    <AddBundleLineButton
                      {...classNames(
                        hasError(
                          snap.context.validationErrors,
                          "missing-lines"
                        ) && "error-outline"
                      )}
                      data-testid="add-bundleline"
                      onClick={() => send({ type: "openProductPicker" })}
                    />
                    {hasError(
                      snap.context.validationErrors,
                      "missing-lines"
                    ) && <Hint>Husk at fylde indhold på bordpakken.</Hint>}
                  </div>
                </Section>

                <Section id="price">
                  <Section.Header>
                    <Section.Title>Pris</Section.Title>
                  </Section.Header>
                  <label htmlFor="price">Pris</label>
                  {snap.matches({ fetchingBundle: "idle" }) && (
                    <PriceInput
                      id="price"
                      value={changedBundle.price}
                      {...classNames(
                        hasError(
                          snap.context.validationErrors,
                          "missing-price"
                        ) && "error-outline"
                      )}
                      data-testid="input-price"
                      onChange={function (amount?: number): void {
                        if (amount) {
                          change({ type: "setPrice", data: amount ?? 0 });
                          refreshSummarySignal.emit();
                        }
                      }}
                    />
                  )}
                  {hasError(snap.context.validationErrors, "missing-price") && (
                    <Hint>Husk at indtaste en pris.</Hint>
                  )}

                  <div>
                    <IonSegment
                      style={{
                        "--ion-text-color": "var(--gm-color-dark-primary)",
                        "--ion-color-primary": "var(--gm-color-dark-primary)",
                        "--color-checked": "var(--gm-color-dark-primary)",
                      }}
                      value={changedBundle.discountStrategy}
                      onIonChange={(ev) => {
                        change({
                          type: "setDiscountStrategy",
                          data: DiscountStrategySchema.parse(ev.detail.value),
                        });
                        refreshSummarySignal.emit();
                      }}
                    >
                      <IonSegmentButton value="Percentage">
                        Procentsats
                      </IonSegmentButton>
                      <IonSegmentButton value="Fixed">
                        Fastsats
                      </IonSegmentButton>
                    </IonSegment>
                    {changedBundle.discountStrategy === "Percentage" ? (
                      <div className={css.DiscountInput}>
                        <label htmlFor="discount-pct">
                          Rabatsats{" "}
                          {changedBundle.discountPercentage &&
                            `(gæsten sparer ${formatMoney(
                              (changedBundle.discountPercentage / 100) *
                                changedBundle.price
                            )} kr.)`}
                        </label>
                        <PercentInput
                          id="discount-pct"
                          data-testid="input-discount"
                          style={{ padding: "1rem" }}
                          onChange={(pct) => {
                            if (pct) {
                              change({
                                type: "setDiscountPercentage",
                                data: pct,
                              });
                            } else {
                              change({
                                type: "setDiscountPercentage",
                                data: undefined,
                              });
                            }
                            refreshSummarySignal.emit();
                          }}
                          value={changedBundle.discountPercentage}
                        />
                      </div>
                    ) : (
                      <div className={css.DiscountInput}>
                        <label htmlFor="discount-fixed"> Rabatsats</label>
                        <PriceInput
                          value={changedBundle.discountFixed}
                          onChange={(amount?: number) =>
                            change({ type: "setDiscountFixed", data: amount })
                          }
                        />
                      </div>
                    )}
                  </div>

                  {hasError(
                    snap.context.validationErrors,
                    "discount-price-larger-than-price"
                  ) && (
                    <Hint>
                      Rabat prisen kan ikke være højere end normalprisen.
                    </Hint>
                  )}
                </Section>

                <BundleSummary
                  api={props.api}
                  refreshSignal={refreshSummarySignal}
                  bundle={changedBundle}
                />

                <Section>
                  <Section.Header>
                    <Section.Title>Specielle priser</Section.Title>
                    <Section.SubTitle>
                      Specielle priser til bestemte perioder. Brugbart til eks.
                      nytår, eller j-dag
                    </Section.SubTitle>
                  </Section.Header>
                  <SpecialPricePeriodEditor
                    api={props.api}
                    data-testid="special-price-editor"
                    mode={snap.context.bundleId ? "update" : "create"}
                    productId={changedBundle.id}
                    onChange={(changes) => {
                      send({
                        type: "setSpecialPricePeriodChangeset",
                        data: changes,
                      });
                    }}
                  />
                </Section>

                <Section>
                  <Section.Header>
                    <Section.Title>Kun aktiv i tidsrum</Section.Title>
                    <Section.SubTitle>
                      Hvis der er tider heri, vil bordpakken <i>kun</i> være
                      aktiv i disse perioder
                    </Section.SubTitle>
                  </Section.Header>
                  <ActivePeriodsEditor
                    api={props.api}
                    onChange={(changeset) => {
                      send({
                        type: "setActivePeriodsChangeset",
                        data: changeset,
                      });
                    }}
                    mode={snap.context.bundleId ? "update" : "create"}
                    productId={changedBundle.id}
                  />
                </Section>

                <Section>
                  <Section.Header>
                    <Section.Title>Indstillinger</Section.Title>
                  </Section.Header>
                  <Option>
                    <Option.Header>
                      <Option.Title>Offentliggjort</Option.Title>
                      <Option.SubTitle>
                        Hvor vidt gæsten kan se bordpakken
                      </Option.SubTitle>
                    </Option.Header>
                    <IonToggle
                      data-testid="toggle-ispublic"
                      mode="ios"
                      checked={changedBundle?.isPublic ?? true}
                      onIonChange={(ev) => {
                        change({
                          type: "setIsPublic",
                          data: ev.detail.checked,
                        });
                      }}
                    />
                  </Option>
                  {snap.context.onlinePOSIntegration?.enabled && (
                    <Option>
                      <Option.Header>
                        <Option.Title>OnlinePOS produkt ID</Option.Title>
                        <Option.SubTitle>
                          Brugt til synkronisering mellem onlinePOS og Greeter
                        </Option.SubTitle>
                      </Option.Header>

                      <TextInput
                        value={snap.context.onlinePOSProductId}
                        type="number"
                        onChange={(e) => {
                          send({
                            type: "setOnlinePOSProductId",
                            data: {
                              onlinePOSProductId:
                                e.currentTarget.value === ""
                                  ? null
                                  : parseInt(e.currentTarget.value),
                            },
                          });
                        }}
                      />
                    </Option>
                  )}
                </Section>
              </section>
              <div
                style={{
                  padding: "1rem 0",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <GradientButton
                  data-testid="save-bundle"
                  style={{ width: "100%" }}
                  onClick={() => {
                    send({ type: "validate" });
                    send({ type: "save" });
                  }}
                >
                  <h3 style={{ margin: 0 }}>Gem bordpakke</h3>
                </GradientButton>

                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    margin: "1rem 0",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    alert({
                      mode: "ios",
                      header: "Du er ved at slette bordpakken",
                      message:
                        "Hvis du trykker ja her, vil du slette bordpakken og dets linjer. Bordpakken kan derefter ikke gendannes. Er du sikker?",
                      buttons: [
                        {
                          text: "Ja, slet bordpakke",
                          handler() {
                            send({ type: "delete" });
                          },
                        },
                        { text: "Nej, fortryd" },
                      ],
                    });
                  }}
                >
                  <DangerText>Slet bordpakke</DangerText>
                </div>
              </div>
            </div>
            <IonModal
              isOpen={snap.value.ui === "pickingProducts"}
              onDidDismiss={() => {
                if (snap.value.ui) {
                  send({ type: "back" });
                }
              }}
            >
              <IonContent>
                <ProductLibrary
                  api={props.api}
                  onExit={(args) => {
                    console.group("[EditBundlePage][ProductLibrary][onPick]");
                    console.debug("[args]", args);
                    if (args.selectedProducts.length > 0) {
                      change({
                        type: "addBundleLineVariant",
                        data: {
                          bundleLineId: snap.context.pickingForBundleLineId,
                          products: args.selectedProducts.map((pp) => ({
                            id: pp.productId,
                            categoryName: pp.categoryName,
                            name: pp.productName,
                          })),
                        },
                      });
                    }
                    send({ type: "back" });
                    refreshSummarySignal.emit();
                    console.groupEnd();
                  }}
                  onSaved={() => send({ type: "back" })}
                />
              </IonContent>
            </IonModal>
          </PageContainer>
          <Spinner
            reason={
              snap.matches({ fetchingBundle: "fetching" })
                ? "Henter bordpakke"
                : snap.matches({ deleteBundle: "deleting" })
                ? "Sletter bordpakke"
                : (snap.matches({ saveBundle: "saving" }) ||
                    snap.matches({ saveBundle: "savingActivePeriods" }) ||
                    snap.matches({ saveBundle: "deletingActivePeriods" }) ||
                    snap.matches({ saveBundle: "savingSpecialPricePeriods" }) ||
                    snap.matches({
                      saveBundle: "deletingSpecialPricePeriods",
                    })) &&
                  "Gemmer bordpakken"
            }
          />
          <IonAlert
            header="Kunne ikke hente bordpakke"
            message="Det er nødvendigt at kunne hente bordpakken for at kunne rette den"
            buttons={[
              {
                text: "Tilbage",
                handler() {
                  history.goBack();
                },
              },
              {
                text: "Prøv igen",
                handler() {
                  send({ type: "fetchBundle" });
                },
              },
            ]}
            isOpen={canShowAlert && snap.matches({ fetchingBundle: "failed" })}
          />
        </DndAutoScrollFixContainer>
      </IonContent>
    </IonPage>
  );
}
